import { useCallback, useEffect, useRef, useState } from "react";

import {
  definitionForActiveReportGroup,
  isDefinitionPersistable,
  mergeActiveGroupIntoDefinition,
  normalizeReportDefinition,
  type ReportLayoutDefinition,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";

export function useReportDefinitionEditor({
  apiDefinition,
  normalizedDefinition,
  activeLayoutId,
  activeReportTabId,
  saveDefinition,
}: {
  apiDefinition: ReportLayoutDefinition | null | undefined;
  normalizedDefinition: ReportLayoutDefinition | null | undefined;
  activeLayoutId: string | null;
  activeReportTabId: string | null;
  saveDefinition: (
    layoutId: string,
    definition: ReportLayoutDefinition,
  ) => Promise<unknown>;
}) {
  const [localDefinition, setLocalDefinition] =
    useState<ReportLayoutDefinition | null>(null);
  const localDefinitionRef = useRef<ReportLayoutDefinition | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDefinitionRef = useRef<{
    layoutId: string;
    definition: ReportLayoutDefinition;
  } | null>(null);
  const fullDefinitionRef = useRef<ReportLayoutDefinition | null>(null);
  // How many edits have been made, so a save that lands knows whether it is
  // still the latest word.
  const editsRef = useRef(0);
  const layoutScopeKey = `${activeLayoutId ?? ""}:${activeReportTabId ?? ""}`;
  const layoutScopeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (layoutScopeKeyRef.current !== layoutScopeKey) {
      layoutScopeKeyRef.current = layoutScopeKey;
      const next = apiDefinition ?? null;
      localDefinitionRef.current = next;
      setLocalDefinition(next);
      fullDefinitionRef.current = normalizedDefinition ?? null;
      return;
    }
    setLocalDefinition((prev) => {
      if (prev) return prev;
      const next = apiDefinition ?? null;
      localDefinitionRef.current = next;
      return next;
    });
  }, [apiDefinition, layoutScopeKey, normalizedDefinition]);

  const definition = localDefinition ?? apiDefinition;

  /**
   * Save, then read the report back as the server keeps it.
   *
   * A widget added here is identified by whatever the browser called it, and
   * the server answers with an identifier of its own - so the canvas asking
   * for that widget's content was asking about a widget nobody had heard of,
   * and the card sat on its skeleton for good. Letting go of the local copy
   * once it is saved is what makes the card the server's widget. An edit made
   * while the save was in flight keeps the local copy: it is the newer word.
   */
  const save = useCallback(
    async (layoutId: string, next: ReportLayoutDefinition) => {
      const savedAt = editsRef.current;
      await saveDefinition(layoutId, next);
      if (editsRef.current !== savedAt) return;
      localDefinitionRef.current = null;
      setLocalDefinition(null);
    },
    [saveDefinition],
  );

  const flushPendingDefinitionSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingDefinitionRef.current;
    if (!pending) return;
    pendingDefinitionRef.current = null;
    if (!isDefinitionPersistable(pending.definition)) return;
    void save(pending.layoutId, pending.definition);
  }, [save]);

  const persistDefinition = useCallback(
    (next: ReportLayoutDefinition) => {
      if (!activeLayoutId) return;
      if (!isDefinitionPersistable(next)) {
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        pendingDefinitionRef.current = null;
        return;
      }
      pendingDefinitionRef.current = {
        layoutId: activeLayoutId,
        definition: next,
      };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const pending = pendingDefinitionRef.current;
        if (!pending) return;
        pendingDefinitionRef.current = null;
        if (!isDefinitionPersistable(pending.definition)) return;
        void save(pending.layoutId, pending.definition);
      }, 600);
    },
    [activeLayoutId, save],
  );

  useEffect(
    () => () => {
      flushPendingDefinitionSave();
    },
    [flushPendingDefinitionSave],
  );

  const updateDefinition = useCallback(
    (updater: (prev: ReportLayoutDefinition) => ReportLayoutDefinition) => {
      const tabScoped = localDefinitionRef.current ?? apiDefinition;
      if (!tabScoped) return;

      editsRef.current += 1;
      const nextTabScoped = normalizeReportDefinition(updater(tabScoped));
      const full = fullDefinitionRef.current ?? normalizedDefinition;

      let nextFull: ReportLayoutDefinition;
      let nextLocal: ReportLayoutDefinition;

      if (!full?.groups?.length) {
        nextFull = nextTabScoped;
        nextLocal = nextTabScoped;
      } else {
        nextFull = mergeActiveGroupIntoDefinition(
          full,
          nextTabScoped,
          activeReportTabId,
        );
        nextLocal = definitionForActiveReportGroup(nextFull, activeReportTabId);
      }

      fullDefinitionRef.current = nextFull;
      localDefinitionRef.current = nextLocal;
      persistDefinition(nextFull);
      setLocalDefinition(nextLocal);
    },
    [activeReportTabId, apiDefinition, normalizedDefinition, persistDefinition],
  );

  return {
    definition,
    updateDefinition,
    flushPendingDefinitionSave,
  };
}
